import {
  FileParameter,
  FileProxy,
  InquiryCorrespondenceDTO,
  InquiryProxy,
  InquiryViewModel,
  LookupProxy,
} from '@/app/proxy/proxies';
import { SharedModule } from '@/app/shared/shared.module';
import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Popover } from 'primeng/popover';
import { FileUploadHandlerEvent, FileSelectEvent, FileUploadErrorEvent, FileUploadEvent } from 'primeng/fileupload';
import { UploadFileAcceptedFileTypes, UploadFileMaxFileSize } from '@/app/shared/consts/comms-consts';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { SkeletonComponent } from '@/app/features/shared/components/skeleton/skeleton.component';
import { DateTime } from 'luxon';
import { SeedGenericIconComponent } from '../../../../components/icons/seed-generic-icon.component';
import { UtcToLocalDatePipe, DateFormatOptions } from '@/app/shared/pipes/utc-to-local-date.pipe';
import { marked } from 'marked';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SeedIconNativeComponent } from '@/app/components/icons/seed-icon-native.component';
import { DkTypingComponent } from '@/app/shared/components/dk-typing/dk-typing.component';
import { Subscription } from 'rxjs';
import {
  InquiryStreamEvent,
  InquiryStreamRequest,
  InquiryStreamService,
} from '@/app/core/services/inquiry-stream.service';

const MAX_STREAM_ATTEMPTS = 3;
const STREAM_RETRY_BACKOFF_MS = [500, 1000, 2000];
const OPTIMISTIC_USER_PREFIX = 'local-user-';
const OPTIMISTIC_AI_PREFIX = 'local-ai-';

// Typewriter pacing: reveal the buffered AI answer to the DOM at a
// human-readable speed, even if the backend delivers content in bursts.
const TYPEWRITER_TICK_MS = 16; // ~60fps render cadence
const TYPEWRITER_BASE_CHARS_PER_TICK = 2; // ~125 cps baseline
const TYPEWRITER_MAX_CATCHUP_LAG = 240; // above this backlog, accelerate

@Component({
  selector: 'dk-support-chat-panel',
  imports: [SharedModule, TranslateModule, SkeletonComponent, SeedGenericIconComponent, UtcToLocalDatePipe, SeedIconNativeComponent, DkTypingComponent],
  templateUrl: './chat-panel.component.html',
  styleUrl: './chat-panel.component.less',
  providers: [InquiryProxy, FileProxy, LookupProxy],
})
export class ChatPanelComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('uploadFilePopover') uploadFilePopover!: Popover;
  @ViewChild('messageContainer') messagesContainer!: ElementRef;
  @Input() inquiryId: string | undefined = undefined;

  informationBoxActive = false;
  informationBoxText = '';

  // Track previous message count to detect new messages
  private previousMessageCount = 0;

  // Track message IDs that were forwarded to expert
  forwardedMessageIds = new Set<string>();

  // Streaming state
  isStreaming = false;
  showTypingIndicator = false;
  private streamSubscription: Subscription | null = null;
  private currentOptimisticAiId: string | null = null;
  private currentOptimisticUserId: string | null = null;
  private aiBuffer = '';
  private firstChunkReceived = false;
  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Typewriter state: `aiBuffer` is what the backend has delivered so far
  // (the "target"); `displayedContent` is what has been revealed to the DOM.
  private displayedContent = '';
  private typewriterTimer: ReturnType<typeof setInterval> | null = null;
  private streamClosed = false;

  // File upload configuration
  maxFileSize = UploadFileMaxFileSize;
  acceptedFileTypes = UploadFileAcceptedFileTypes;
  uploadedFileId: string = '';

  uploadedFile: File = new File([], '');

  inquiryFileUploadTypeId: string = '';

  messageHasUploadedFile = false;
  sendingMessage = false;

  messageForm: FormGroup = new FormGroup({
    message: new FormControl('', [Validators.minLength(1)]),
  });

  inquiry: InquiryViewModel = new InquiryViewModel();

  loadingInquiry = false;

  // Cache for rendered markdown content - accessible as object in template
  renderedContents: { [key: string]: SafeHtml } = {};

  // Custom skeletons are now defined inline in template
  // No need for generic skeleton configs anymore

  // Date format options for header
  headerDateFormat: DateFormatOptions = {
    showTimeAgo: false,
    simpleFormat: { month: 'long', day: '2-digit', year: 'numeric' },
  };

  constructor(
    private fileProxy: FileProxy,
    private inquiryProxy: InquiryProxy,
    private lookupProxy: LookupProxy,
    private sanitizer: DomSanitizer,
    private inquiryStreamService: InquiryStreamService
  ) {}

  ngOnInit(): void {
    this.getInquiry();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inquiryId'] != undefined) {
      this.cancelStream();
      this.getInquiry();
    }
  }

  private scrollToBottom(force: boolean = false): void {
    if (!this.messagesContainer?.nativeElement) {
      return;
    }

    const element = this.messagesContainer.nativeElement;
    const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 100;

    // Only auto-scroll if user is near bottom or if forced (new message sent)
    if (force || isNearBottom) {
      setTimeout(() => {
        element.scrollTop = element.scrollHeight;
      }, 0);
    }
  }

  getInquiry(withoutLoading: boolean = false) {
    if (!withoutLoading) {
      this.loadingInquiry = true;
    }

    // Load file types first
    this.lookupProxy.lookup_GetFileTypes().subscribe({
      next: (response) => {
        this.inquiryFileUploadTypeId = response.result?.find((item) => item.value === 'Inquiry')?.key || '';
      },
      error: (error) => {
        console.error('Failed to load file types:', error);
      },
    });

    // Load inquiry data
    if (this.inquiryId) {
      this.inquiryProxy.inquiry_Get(this.inquiryId).subscribe({
        next: (response) => {
          const serverInquiry = response.result || new InquiryViewModel();
          const serverCorrespondences = (serverInquiry.inquiryCorrespondences || [])
            .filter(
              (item) => item.inquiryContent != null && item.inquiryContent != undefined && item.inquiryContent != ''
            )
            .sort((a, b) => a.createdAt.toMillis() - b.createdAt.toMillis());

          // Preserve in-flight optimistic bubbles while streaming so the UI
          // does not flicker when a mid-stream reconcile fetch lands.
          const optimisticMessages = this.isStreaming
            ? (this.inquiry.inquiryCorrespondences || []).filter((message) =>
                this.isOptimisticMessageId(message.id)
              )
            : [];

          this.inquiry = serverInquiry;
          this.inquiry.inquiryCorrespondences = [...serverCorrespondences, ...optimisticMessages];

          // Track messages that were forwarded to expert
          this.inquiry.inquiryCorrespondences.forEach((message) => {
            if (message.forwardedToExpert) {
              this.forwardedMessageIds.add(message.id);
            }
          });

          this.processMarkdownForMessages();

          // Check if there are new messages
          const currentMessageCount = this.inquiry.inquiryCorrespondences?.length || 0;
          const hasNewMessages = currentMessageCount > this.previousMessageCount;
          this.previousMessageCount = currentMessageCount;

          if (hasNewMessages) {
            this.scrollToBottom(true);
          }

          this.loadingInquiry = false;
        },
        error: (error) => {
          console.error('Failed to load inquiry:', error);
          this.loadingInquiry = false;
        },
      });
    } else {
      this.loadingInquiry = false;
    }
  }

  openAttachPopover(target: any) {
    if (this.uploadFilePopover) {
      this.uploadFilePopover.toggle(target);
    }
  }

  onFileSelect(event: FileSelectEvent) {
    console.log('File selected:', event.files);
    // Handle file selection if needed
  }

  onFileUpload(event: FileUploadEvent) {
    console.log('File uploaded successfully:', event);
    // Handle successful upload
    this.uploadFilePopover.hide();
  }

  onFileUploadError(event: FileUploadErrorEvent) {
    console.log('File upload error:', event);
    // Handle upload error
  }

  customUploadHandler(event: FileUploadHandlerEvent) {
    const file = event.files[0];
    let fileParameter: FileParameter = {
      data: file,
      fileName: file.name,
    };

    this.fileProxy.file_Upload(this.inquiryFileUploadTypeId, fileParameter).subscribe({
      next: (response) => {
        this.uploadFilePopover.hide();
        this.uploadedFileId = response.result?.id || '';
        this.uploadedFile = file;
        this.messageHasUploadedFile = true;
        event.files.splice(0, event.files.length);
      },
      error: (error) => {
        console.error('Upload failed:', error);
      },
    });
  }

  sendMessage() {
    if (!this.canSendMessage || !this.inquiryId) {
      return;
    }

    const content: string = this.messageForm.get('message')?.value || '';
    const uploadedFileId = this.messageHasUploadedFile ? this.uploadedFileId : undefined;
    const fileType = this.messageHasUploadedFile ? 'Image' : undefined;
    const fileUrl = this.messageHasUploadedFile ? this.getUploadedFilePreviewUrl() : undefined;

    this.appendOptimisticUserMessage(content, fileType, fileUrl);
    this.appendOptimisticAiPlaceholder();
    this.clearMessage();
    this.scrollToBottom(true);

    const request: InquiryStreamRequest = {
      inquiryId: this.inquiryId,
      inquiryContent: content,
      uploadedFileId,
      fileType,
    };

    this.startStream(request, 0);
  }

  private startStream(request: InquiryStreamRequest, attempt: number): void {
    this.cancelStreamSubscription();
    this.stopTypewriter();
    this.isStreaming = true;
    this.showTypingIndicator = true;
    this.firstChunkReceived = false;
    this.sendingMessage = true;
    this.streamClosed = false;
    this.displayedContent = '';

    let errored = false;

    this.streamSubscription = this.inquiryStreamService.stream(request).subscribe({
      next: (event) => this.handleStreamEvent(event, () => (errored = true)),
      error: () => {
        this.streamClosed = true;
        this.handleStreamFailure(request, attempt);
      },
      complete: () => {
        this.streamClosed = true;
        if (errored) {
          this.handleStreamFailure(request, attempt);
          return;
        }
        this.waitForTypewriterThen(() => this.finalizeStreamSuccess());
      },
    });
  }

  private handleStreamEvent(event: InquiryStreamEvent, markErrored: () => void): void {
    switch (event.type) {
      case 'metadata':
        this.rekeyOptimisticAiMessage(event.inquiryCorrespondenceId);
        break;
      case 'chunk':
        if (!this.firstChunkReceived) {
          this.firstChunkReceived = true;
          this.showTypingIndicator = false;
        }
        this.aiBuffer += event.content ?? '';
        this.ensureTypewriter();
        break;
      case 'citations':
        // Use citations as the authoritative final text — it may differ
        // slightly from the concatenated chunks (whitespace normalization etc.).
        this.aiBuffer = event.answer ?? this.aiBuffer;
        this.ensureTypewriter();
        break;
      case 'error':
        markErrored();
        break;
      case 'done':
        break;
    }
  }

  private ensureTypewriter(): void {
    if (this.typewriterTimer) {
      return;
    }
    this.typewriterTimer = setInterval(() => this.tickTypewriter(), TYPEWRITER_TICK_MS);
  }

  private stopTypewriter(): void {
    if (this.typewriterTimer) {
      clearInterval(this.typewriterTimer);
      this.typewriterTimer = null;
    }
  }

  private tickTypewriter(): void {
    const backlog = this.aiBuffer.length - this.displayedContent.length;
    if (backlog <= 0) {
      if (this.streamClosed) {
        this.stopTypewriter();
      }
      return;
    }

    // Catch up gracefully when the backend delivers a large burst at once
    // by scaling chars-per-tick with the backlog, capped so small replies
    // still read smoothly.
    const catchupMultiplier = Math.max(
      1,
      Math.ceil(backlog / TYPEWRITER_MAX_CATCHUP_LAG)
    );
    const charsThisTick = Math.min(
      backlog,
      TYPEWRITER_BASE_CHARS_PER_TICK * catchupMultiplier
    );

    this.displayedContent = this.aiBuffer.slice(
      0,
      this.displayedContent.length + charsThisTick
    );
    this.updateStreamingAiContent(this.displayedContent);
    this.scrollToBottom(false);
  }

  private waitForTypewriterThen(callback: () => void): void {
    if (!this.typewriterTimer && this.displayedContent.length >= this.aiBuffer.length) {
      callback();
      return;
    }
    // Poll briefly; the typewriter drains within a few hundred ms.
    const waitInterval = setInterval(() => {
      if (this.displayedContent.length >= this.aiBuffer.length) {
        clearInterval(waitInterval);
        this.stopTypewriter();
        callback();
      }
    }, TYPEWRITER_TICK_MS);
  }

  private handleStreamFailure(request: InquiryStreamRequest, attempt: number): void {
    this.cancelStreamSubscription();

    const nextAttempt = attempt + 1;
    if (nextAttempt < MAX_STREAM_ATTEMPTS) {
      const delay = STREAM_RETRY_BACKOFF_MS[attempt] ?? 2000;
      this.stopTypewriter();
      this.aiBuffer = '';
      this.displayedContent = '';
      this.updateStreamingAiContent('');
      this.showTypingIndicator = true;
      this.retryTimeoutId = setTimeout(() => {
        this.retryTimeoutId = null;
        this.startStream(request, nextAttempt);
      }, delay);
      return;
    }

    // Exhausted retries: drop the placeholder and let the backend-driven
    // forwarded-to-expert state render via the normal inquiry fetch.
    this.removeOptimisticAiMessage();
    this.resetStreamingFlags();
    if (this.inquiryId) {
      this.getInquiry(true);
    }
  }

  private finalizeStreamSuccess(): void {
    this.resetStreamingFlags();
    if (this.inquiryId) {
      this.getInquiry(true);
    }
  }

  private resetStreamingFlags(): void {
    this.stopTypewriter();
    this.isStreaming = false;
    this.showTypingIndicator = false;
    this.firstChunkReceived = false;
    this.sendingMessage = false;
    this.currentOptimisticAiId = null;
    this.currentOptimisticUserId = null;
    this.aiBuffer = '';
    this.displayedContent = '';
    this.streamClosed = false;
  }

  private cancelStream(): void {
    this.cancelStreamSubscription();
    this.stopTypewriter();
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
    this.removeOptimisticAiMessage();
    this.resetStreamingFlags();
  }

  private cancelStreamSubscription(): void {
    if (this.streamSubscription) {
      this.streamSubscription.unsubscribe();
      this.streamSubscription = null;
    }
  }

  private appendOptimisticUserMessage(
    content: string,
    fileType: string | undefined,
    fileUrl: string | undefined
  ): void {
    const id = `${OPTIMISTIC_USER_PREFIX}${this.generateLocalId()}`;
    const message = new InquiryCorrespondenceDTO();
    message.id = id;
    message.inquiryContent = content;
    (message as { fileType?: string }).fileType = fileType;
    message.fileUrl = fileUrl;
    message.userId = '';
    message.userName = '';
    message.correspondenceType = 'User';
    message.correspondenceTypeId = '';
    message.forwardedToExpert = false;
    message.isByMe = true;
    message.isRetry = false;
    message.createdAt = DateTime.utc();

    this.inquiry.inquiryCorrespondences = [
      ...(this.inquiry.inquiryCorrespondences || []),
      message,
    ];
    this.currentOptimisticUserId = id;
    this.previousMessageCount = this.inquiry.inquiryCorrespondences.length;
  }

  private appendOptimisticAiPlaceholder(): void {
    const id = `${OPTIMISTIC_AI_PREFIX}${this.generateLocalId()}`;
    const message = new InquiryCorrespondenceDTO();
    message.id = id;
    message.inquiryContent = '';
    message.userId = '';
    message.userName = '';
    message.correspondenceType = 'AI';
    message.correspondenceTypeId = '';
    message.forwardedToExpert = false;
    message.isByMe = false;
    message.isRetry = false;
    message.createdAt = DateTime.utc();

    this.inquiry.inquiryCorrespondences = [
      ...(this.inquiry.inquiryCorrespondences || []),
      message,
    ];
    this.currentOptimisticAiId = id;
    this.renderedContents[id] = this.sanitizer.bypassSecurityTrustHtml('');
    this.aiBuffer = '';
    this.previousMessageCount = this.inquiry.inquiryCorrespondences.length;
  }

  private rekeyOptimisticAiMessage(serverId: string): void {
    if (!this.currentOptimisticAiId || !serverId) {
      return;
    }
    const existing = this.inquiry.inquiryCorrespondences?.find(
      (m) => m.id === this.currentOptimisticAiId
    );
    if (!existing) {
      return;
    }
    const previousId = existing.id;
    existing.id = serverId;
    if (this.renderedContents[previousId]) {
      this.renderedContents[serverId] = this.renderedContents[previousId];
      delete this.renderedContents[previousId];
    }
    this.currentOptimisticAiId = serverId;
  }

  private updateStreamingAiContent(content: string): void {
    if (!this.currentOptimisticAiId) {
      return;
    }
    const message = this.inquiry.inquiryCorrespondences?.find(
      (m) => m.id === this.currentOptimisticAiId
    );
    if (!message) {
      return;
    }
    message.inquiryContent = content;
    const html = marked.parse(content || '') as string;
    this.renderedContents[message.id] = this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private removeOptimisticAiMessage(): void {
    if (!this.currentOptimisticAiId) {
      return;
    }
    const id = this.currentOptimisticAiId;
    if (this.inquiry?.inquiryCorrespondences) {
      this.inquiry.inquiryCorrespondences = this.inquiry.inquiryCorrespondences.filter(
        (m) => m.id !== id
      );
    }
    delete this.renderedContents[id];
    this.currentOptimisticAiId = null;
  }

  private isOptimisticMessageId(id: string | undefined | null): boolean {
    if (!id) return false;
    return id.startsWith(OPTIMISTIC_AI_PREFIX) || id.startsWith(OPTIMISTIC_USER_PREFIX);
  }

  private generateLocalId(): string {
    const cryptoRef = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (cryptoRef?.randomUUID) {
      return cryptoRef.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  private getUploadedFilePreviewUrl(): string | undefined {
    if (!this.uploadedFile || !this.uploadedFile.size) {
      return undefined;
    }
    try {
      return URL.createObjectURL(this.uploadedFile);
    } catch {
      return undefined;
    }
  }

  clearMessage() {
    this.messageHasUploadedFile = false;
    this.uploadedFileId = '';
    this.uploadedFile = new File([], '');
    this.messageForm.get('message')?.setValue('');
    this.messageForm.markAsUntouched();
  }

  get hasForwardedMessage(): boolean {
    if (!this.inquiry.inquiryCorrespondences || this.inquiry.inquiryCorrespondences.length === 0) {
      return false;
    }

    return this.inquiry.inquiryCorrespondences.some(message =>
      message.forwardedToExpert === true || this.forwardedMessageIds.has(message.id)
    );
  }

  get canSendMessage(): boolean {
    const hasMessage = this.messageForm.get('message')?.value.length > 0;
    const isNotSending = !this.sendingMessage;
    const isNotLoading = !this.loadingInquiry;
    const noForwardedMessage = !this.hasForwardedMessage;
    const isNotStreaming = !this.isStreaming;

    return hasMessage && isNotSending && isNotLoading && noForwardedMessage && isNotStreaming;
  }

  formatMessageDate(createdAt: DateTime): string {
    if (!createdAt || !createdAt.isValid) {
      return '';
    }

    const now = DateTime.local();
    const messageDate = createdAt.toLocal();

    // Check if year is different
    if (messageDate.year !== now.year) {
      // Format: June 16, 2025 at 09:13
      return messageDate.toFormat('MMMM d, yyyy \'at\' HH:mm');
    }

    if (messageDate.hasSame(now, 'day')) {
      // Format: 09:13
      return messageDate.toFormat('HH:mm');
    }

    // Same year but different day/month
    // Format: September 22, 12:32
    return messageDate.toFormat('MMMM d, HH:mm');
  }

  private processMarkdownForMessages(): void {
    // Preserve in-flight streaming bubble HTML so we don't lose mid-stream
    // markdown while a reconcile fetch lands.
    const preserved: { [key: string]: SafeHtml } = {};
    if (this.currentOptimisticAiId && this.renderedContents[this.currentOptimisticAiId]) {
      preserved[this.currentOptimisticAiId] = this.renderedContents[this.currentOptimisticAiId];
    }

    this.renderedContents = {};
    if (!this.inquiry.inquiryCorrespondences) {
      Object.assign(this.renderedContents, preserved);
      return;
    }

    this.inquiry.inquiryCorrespondences.forEach((message) => {
      if (message.correspondenceType === 'AI' && message.inquiryContent) {
        const html = marked.parse(message.inquiryContent) as string;
        this.renderedContents[message.id] = this.sanitizer.bypassSecurityTrustHtml(html);
      }
    });

    Object.assign(this.renderedContents, preserved);
  }

  isMessageForwarded(messageId: string): boolean {
    // Check if we've tracked this message ID as forwarded
    if (this.forwardedMessageIds.has(messageId)) {
      return true;
    }

    // Also check if the message itself has forwardedToExpert property
    const message = this.inquiry.inquiryCorrespondences?.find(m => m.id === messageId);
    return message?.forwardedToExpert === true;
  }

  private getLastMessage() {
    const correspondences = this.inquiry.inquiryCorrespondences || [];
    return correspondences.length > 0 ? correspondences[correspondences.length - 1] : null;
  }

  isLastMessageFromUser(): boolean {
    const lastMessage = this.getLastMessage();
    return lastMessage?.isByMe === true;
  }

  isLastMessageForwarded(): boolean {
    const lastMessage = this.getLastMessage();
    if (!lastMessage) {
      return false;
    }
    return this.isMessageForwarded(lastMessage.id);
  }

  ngOnDestroy(): void {
    this.cancelStream();
  }
}
